import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { notificationService } from '../services/notifications';

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const userRef = useRef(user);
    const intervalRef = useRef(null);
    const isActiveRef = useRef(true); // Track if component is mounted

    // Update user ref when user changes
    useEffect(() => {
        userRef.current = user;
        // Set isActiveRef to true when user is available
        if (user) {
            isActiveRef.current = true;
        }
    }, [user]);

    // Fetch notifications
    const fetchNotifications = useCallback(async () => {
        if (!userRef.current || !isActiveRef.current) {
            return;
        }
        
        try {
            setLoading(true);
            setError(null);
            const data = await notificationService.getRecentNotifications();
            if (isActiveRef.current) {
                setNotifications(data);
            }
        } catch (err) {
            if (isActiveRef.current) {
                setError(err.message);
            }
        } finally {
            if (isActiveRef.current) {
                setLoading(false);
            }
        }
    }, []);

    // Fetch unread count
    const fetchUnreadCount = useCallback(async () => {
        if (!userRef.current || !isActiveRef.current) {
            return;
        }
        
        try {
            const data = await notificationService.getUnreadCount();
            if (isActiveRef.current) {
                setUnreadCount(data.unread_count);
            }
        } catch (err) {
            // Silently handle errors for unread count
        }
    }, []);

    // Mark notification as read
    const markAsRead = useCallback(async (notificationId) => {
        if (!isActiveRef.current) return;
        
        try {
            await notificationService.markAsRead(notificationId);
            
            // Update local state
            setNotifications(prev => 
                prev.map(notification => 
                    notification.id === notificationId 
                        ? { ...notification, is_read: true }
                        : notification
                )
            );
            
            // Update unread count
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Error marking notification as read:', err);
        }
    }, []);

    // Mark all notifications as read
    const markAllAsRead = useCallback(async () => {
        if (!isActiveRef.current) return;
        
        try {
            await notificationService.markAllAsRead();
            
            // Update local state
            setNotifications(prev => 
                prev.map(notification => ({ ...notification, is_read: true }))
            );
            
            // Update unread count
            setUnreadCount(0);
        } catch (err) {
            console.error('Error marking all notifications as read:', err);
        }
    }, []);

    // Add new notification (for real-time updates)
    const addNotification = useCallback((notification) => {
        if (!isActiveRef.current) return;
        
        setNotifications(prev => [notification, ...prev]);
        if (!notification.is_read) {
            setUnreadCount(prev => prev + 1);
        }
    }, []);

    // Manual refresh function for immediate updates
    const refreshNotifications = useCallback(async () => {
        if (!isActiveRef.current) return;
        
        await Promise.all([
            fetchNotifications(),
            fetchUnreadCount()
        ]);
    }, [fetchNotifications, fetchUnreadCount]);

    // Initial fetch - only run once when user changes
    useEffect(() => {
        if (user && !isInitialized) {
            fetchNotifications();
            fetchUnreadCount();
            setIsInitialized(true);
        } else if (!user) {
            // Reset state when user logs out
            setNotifications([]);
            setUnreadCount(0);
            setLoading(false);
            setError(null);
            setIsInitialized(false);
        }
    }, [user, isInitialized]);

    // Optimized polling for new notifications
    useEffect(() => {
        if (!user || !isInitialized) return;

        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        // Set up new interval with 5-minute duration to reduce server load
        intervalRef.current = setInterval(() => {
            if (isActiveRef.current && userRef.current) {
                // Refresh both notifications and unread count every 5 minutes
                fetchNotifications();
                fetchUnreadCount();
            }
        }, 300000); // 5 minutes - reduced frequency to improve performance

        // Cleanup function
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [user, isInitialized, fetchNotifications, fetchUnreadCount]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isActiveRef.current = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);

    const value = {
        notifications,
        unreadCount,
        loading,
        error,
        fetchNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        addNotification,
        refreshNotifications
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}; 